if (!window.__tvtLoaded) { window.__tvtLoaded = true;
/**
 * Ticket Variants Table JavaScript
 * Handles cart functionality for the custom ticket variants table
 */

class TicketVariantsTable {
  constructor() {
    this.selectedVariants = new Map();
    this.init();
  }

  init() {
    this.setupQuantitySelectors();
    this.setupAddToCartButtons();
    this.setupBuyNowButtons();
    this.setupSubtotalUpdates(); // #1: Live-updating price totals
    // Bulk selection disabled - checkboxes removed
    // this.setupBulkSelection();
    this.observeCartUpdates();
    this.stampDefaultOrder();
    this.setupSortControls();
    this.initCountdown();
    // Deal scores after short delay so seating-map.js can clone cards first
    setTimeout(() => this.computeDealScores(), 100);

    // Currency: update prices if EUR mode is already active
    if (window.CurrencyConverter && window.CurrencyConverter.isEurMode()) {
      this.updatePricesForCurrency();
    }
    document.addEventListener('currency:ready', () => this.updatePricesForCurrency());

    // Featured card: mark the cheapest is-recommended card as is-featured
    this.markFeaturedCard();
  }

  /**
   * Mark the cheapest is-recommended card as is-featured (only one gets the highlight)
   */
  markFeaturedCard() {
    const recommendedCards = document.querySelectorAll('.ticket-variant-card.is-recommended');
    if (recommendedCards.length === 0) return;

    let cheapest = null;
    let cheapestPrice = Infinity;
    recommendedCards.forEach(card => {
      const price = parseFloat(card.dataset.price);
      if (!isNaN(price) && price < cheapestPrice) {
        cheapestPrice = price;
        cheapest = card;
      }
    });
    if (cheapest) {
      cheapest.classList.add('is-featured');
    }
  }

  /**
   * Get allowed quantities based on "no single ticket left behind" rule
   * Rule: Cannot select quantity that leaves exactly 1 ticket remaining
   * For total inventory T, allowed quantities: [1, 2, ..., T-2, T] (skip T-1)
   * Examples:
   * - T=2: [2] (buying 1 leaves 1)
   * - T=3: [1, 3] (buying 2 leaves 1)
   * - T=4: [1, 2, 4] (buying 3 leaves 1)
   * - T=5: [1, 2, 3, 5] (buying 4 leaves 1)
   */
  getAllowedQuantities(maxInventory) {
    const max = parseInt(maxInventory);

    if (isNaN(max) || max <= 0) {
      return [1]; // Default
    }

    if (max === 1) {
      return [1];
    }

    if (max === 2) {
      return [2]; // Can't buy 1, would leave 1
    }

    // For max >= 3: build list of allowed quantities
    const allowed = [];
    for (let i = 1; i <= max; i++) {
      const remaining = max - i;
      // Skip if it leaves exactly 1 ticket
      if (remaining !== 1) {
        allowed.push(i);
      }
    }

    return allowed;
  }

  /**
   * Replace quantity input with dropdown containing only allowed quantities
   */
  replaceWithDropdown(input) {
    // Skip if already replaced
    if (input.tagName === 'SELECT' || input.classList.contains('qty-dropdown-processed')) {
      return;
    }

    const maxInventory = parseInt(input.getAttribute('max')) || parseInt(input.dataset.max) || 10;
    const currentValue = parseInt(input.value) || 1;
    const allowedQuantities = this.getAllowedQuantities(maxInventory);

    // Create select element
    const select = document.createElement('select');
    select.className = input.className + ' qty-dropdown';
    select.name = input.name;
    select.id = input.id;

    // Copy all data attributes
    Array.from(input.attributes).forEach(attr => {
      if (attr.name.startsWith('data-')) {
        select.setAttribute(attr.name, attr.value);
      }
    });

    // Add options
    let defaultValue = allowedQuantities[0]; // Default to first allowed
    allowedQuantities.forEach(qty => {
      const option = document.createElement('option');
      option.value = qty;
      option.textContent = qty;

      // Try to maintain current value if it's allowed
      if (qty === currentValue) {
        option.selected = true;
        defaultValue = qty;
      }

      select.appendChild(option);
    });

    // Set the selected value
    select.value = defaultValue;

    // Add change listener for bulk selection
    select.addEventListener('change', (e) => {
      const variantId = e.target.dataset.variantId;
      this.updateBulkSelection(variantId, e.target.value);
    });

    // Replace input with select
    input.parentNode.replaceChild(select, input);

    // Hide +/- buttons since we're using dropdown
    const parent = select.parentNode;
    if (parent) {
      parent.querySelectorAll('.qty-minus, .qty-plus, .qty-decrease, .qty-increase').forEach(btn => {
        btn.style.display = 'none';
      });
    }

    console.log('[TicketVariantsTable] Replaced input with dropdown. Max:', maxInventory, 'Allowed:', allowedQuantities.join(', '), 'Selected:', defaultValue);

    return select;
  }

  /**
   * Setup quantity selector buttons
   */
  setupQuantitySelectors() {
    // Setup +/- button handlers using event delegation for dynamic content
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.qty-btn');
      if (!btn) return;

      // Skip if in cart drawer
      if (btn.closest('cart-drawer') || btn.closest('.cart-drawer') || btn.closest('#cart')) {
        return;
      }

      const controls = btn.closest('.quantity-selector') || btn.closest('.qty-controls');
      if (!controls) return;

      const input = controls.querySelector('.qty-input') || controls.querySelector('input[type="number"]');
      if (!input) return;

      const currentValue = parseInt(input.value) || 2;
      const minValue = parseInt(input.min) || 1;
      const maxValue = parseInt(input.max) || 10;

      if (btn.classList.contains('minus') || btn.classList.contains('qty-decrease')) {
        if (currentValue > minValue) {
          input.value = currentValue - 1;
          // #1: Update subtotal when quantity changes
          this.updateSubtotal(input.dataset.variantId, currentValue - 1);
        }
      } else if (btn.classList.contains('plus') || btn.classList.contains('qty-increase')) {
        if (currentValue < maxValue) {
          input.value = currentValue + 1;
          // #1: Update subtotal when quantity changes
          this.updateSubtotal(input.dataset.variantId, currentValue + 1);
        }
      }
    });
  }

  /**
   * #1: Setup subtotal updates - Initialize and listen for quantity changes
   */
  setupSubtotalUpdates() {
    // Listen for manual input changes on quantity fields
    document.querySelectorAll('.qty-input').forEach(input => {
      input.addEventListener('change', (e) => {
        const variantId = e.target.dataset.variantId;
        const quantity = parseInt(e.target.value) || 1;
        this.updateSubtotal(variantId, quantity);
      });
    });
  }

  /**
   * #1: Update subtotal display for a specific variant
   */
  updateSubtotal(variantId, quantity) {
    const subtotalRow = document.querySelector(`.subtotal-row[data-variant-id="${variantId}"]`);
    if (!subtotalRow) return;

    const unitPrice = parseInt(subtotalRow.dataset.unitPrice) || 0;
    const total = unitPrice * quantity;

    // Update quantity display
    const qtyDisplay = subtotalRow.querySelector('[data-qty-display]');
    if (qtyDisplay) {
      qtyDisplay.textContent = `${quantity} ticket${quantity !== 1 ? 's' : ''}`;
    }

    // Update subtotal value
    const subtotalValue = subtotalRow.querySelector('[data-subtotal-value]');
    if (subtotalValue) {
      // Format as money (price is in cents)
      subtotalValue.textContent = this.formatMoney(total);
    }

    // Add brief animation
    subtotalRow.classList.add('updating');
    setTimeout(() => subtotalRow.classList.remove('updating'), 300);
  }

  /**
   * Setup add to cart buttons
   */
  setupAddToCartButtons() {
    document.querySelectorAll('.add-to-cart-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopImmediatePropagation(); // CRITICAL: Stop other scripts from also adding to cart
        const button = e.currentTarget;
        const variantId = button.dataset.variantId;
        const productId = button.dataset.productId;

        // Get quantity - works with both select dropdowns and inputs
        // Check by data-variant-id attribute (for custom table) OR by id (for other places)
        const qtyElement = document.querySelector(`.qty-input[data-variant-id="${variantId}"]`) ||
                          document.querySelector(`.qty-dropdown[data-variant-id="${variantId}"]`) ||
                          document.querySelector(`#qty-${variantId}`) ||
                          document.querySelector(`#mobile-qty-${variantId}`);

        console.log('[TicketVariantsTable] Add to cart clicked');
        console.log('[TicketVariantsTable] variantId:', variantId);
        console.log('[TicketVariantsTable] qtyElement found:', qtyElement);
        console.log('[TicketVariantsTable] qtyElement value:', qtyElement?.value);

        const quantity = parseInt(qtyElement?.value) || 1;
        console.log('[TicketVariantsTable] Quantity to add:', quantity);

        // Add to cart
        await this.addToCart(variantId, quantity, button);
      });
    });
  }

  /**
   * Add item to cart
   */
  async addToCart(variantId, quantity, button) {
    // Show loading state
    button.classList.add('loading');
    button.disabled = true;
    
    try {
      // Create form data for more reliable cart addition
      const formData = {
        items: [{
          id: variantId,
          quantity: parseInt(quantity)
        }]
      };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Cart add failed:', errorData);
        throw new Error('Failed to add to cart');
      }
      
      const data = await response.json();
      console.log('Added to cart:', data);

      // Show success state
      button.classList.remove('loading');
      button.classList.add('success');
      button.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        <span>Added!</span>
      `;

      // Fetch full cart data for the theme's cart drawer
      const cartResponse = await fetch('/cart.js');
      const cartContent = await cartResponse.json();

      // Fetch cart sections for drawer update
      const sectionsResponse = await fetch('/cart?sections=cart-drawer,cart-icon-bubble');
      const sections = await sectionsResponse.json();
      cartContent['sections'] = sections;

      // Dispatch the theme's variant:add event - this is what makes the cart drawer open
      document.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: data.hasOwnProperty('items') ? data['items'] : [data],
          cart: cartContent
        }
      }));

      // Also dispatch cart:change for theme updates
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: cartContent
        }
      }));

      // Update cart UI and bubble
      this.updateCartUI(data);
      await this.updateCartBubble();

      // Reset button after 2 seconds
      setTimeout(() => {
        button.classList.remove('success');
        button.disabled = false;
        button.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 2L3 9H12L18 2M3 9V20C3 21 4 22 5 22H19C20 22 21 21 21 20V9M3 9H21M18 2H6"/>
          </svg>
          <span>Add to Cart</span>
        `;
      }, 2000);

      // Show notification
      this.showNotification(`Added ${quantity} ticket${quantity > 1 ? 's' : ''} to cart!`, 'success');
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      button.classList.remove('loading');
      button.disabled = false;
      this.showNotification('Failed to add to cart. Please try again.', 'error');
    }
  }

  /**
   * Setup buy now buttons - adds to cart and redirects to cart page
   */
  setupBuyNowButtons() {
    document.querySelectorAll('.buy-now-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        const button = e.currentTarget;
        const variantId = button.dataset.variantId;

        // Get quantity
        const qtyElement = document.querySelector(`.qty-input[data-variant-id="${variantId}"]`) ||
                          document.querySelector(`.qty-dropdown[data-variant-id="${variantId}"]`) ||
                          document.querySelector(`#qty-${variantId}`);

        const quantity = parseInt(qtyElement?.value) || 1;

        // Show loading state
        button.classList.add('loading');
        button.disabled = true;

        try {
          // Add to cart
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: parseInt(quantity)
              }]
            })
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          // Redirect to cart page (respects locale: /fr/cart, /es/cart, etc.)
          const localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          window.location.href = localeRoot.replace(/\/$/, '') + '/cart';

        } catch (error) {
          console.error('Error with buy now:', error);
          button.classList.remove('loading');
          button.disabled = false;
          this.showNotification('Failed to add to cart. Please try again.', 'error');
        }
      });
    });
  }

  /**
   * Setup bulk selection functionality
   */
  setupBulkSelection() {
    // Add checkboxes to each row
    const rows = document.querySelectorAll('.variant-row:not(.sold-out-row)');
    
    rows.forEach(row => {
      const variantId = row.dataset.variantId;
      const sectionCell = row.querySelector('.section-column');
      
      if (sectionCell) {
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'variant-checkbox';
        checkbox.dataset.variantId = variantId;
        checkbox.style.marginRight = '10px';
        
        checkbox.addEventListener('change', (e) => {
          this.handleCheckboxChange(e.target);
        });
        
        sectionCell.prepend(checkbox);
      }
    });
    
    // Handle bulk add to cart
    const bulkAddBtn = document.getElementById('bulk-add-to-cart');
    if (bulkAddBtn) {
      bulkAddBtn.addEventListener('click', () => {
        this.bulkAddToCart();
      });
    }
  }

  /**
   * Handle checkbox changes for bulk selection
   */
  handleCheckboxChange(checkbox) {
    const variantId = checkbox.dataset.variantId;
    const row = checkbox.closest('.variant-row');
    
    if (checkbox.checked) {
      row.classList.add('selected');
      const qtyInput = row.querySelector('.qty-input');
      const quantity = parseInt(qtyInput?.value) || 1;
      const price = parseFloat(row.dataset.variantPrice) || 0;
      
      this.selectedVariants.set(variantId, {
        quantity: quantity,
        price: price
      });
    } else {
      row.classList.remove('selected');
      this.selectedVariants.delete(variantId);
    }
    
    this.updateBulkSelectionUI();
  }

  /**
   * Update bulk selection when quantity changes
   */
  updateBulkSelection(variantId, quantity) {
    const checkbox = document.querySelector(`.variant-checkbox[data-variant-id="${variantId}"]`);
    
    if (checkbox && checkbox.checked) {
      const row = checkbox.closest('.variant-row');
      const price = parseFloat(row.dataset.variantPrice) || 0;
      
      this.selectedVariants.set(variantId, {
        quantity: parseInt(quantity),
        price: price
      });
      
      this.updateBulkSelectionUI();
    }
  }

  /**
   * Update bulk selection UI
   */
  updateBulkSelectionUI() {
    const footer = document.getElementById('bulk-selection-footer');
    const countSpan = document.getElementById('selected-count');
    const totalSpan = document.getElementById('selected-total');
    
    if (!footer) return;
    
    if (this.selectedVariants.size > 0) {
      footer.style.display = 'flex';
      
      let totalQuantity = 0;
      let totalPrice = 0;
      
      this.selectedVariants.forEach(item => {
        totalQuantity += item.quantity;
        totalPrice += item.price * item.quantity;
      });
      
      countSpan.textContent = totalQuantity;
      totalSpan.textContent = this.formatMoney(totalPrice);
    } else {
      footer.style.display = 'none';
    }
  }

  /**
   * Bulk add selected items to cart
   */
  async bulkAddToCart() {
    const button = document.getElementById('bulk-add-to-cart');
    if (!button || this.selectedVariants.size === 0) return;
    
    button.disabled = true;
    button.textContent = 'Adding to cart...';
    
    const items = [];
    this.selectedVariants.forEach((item, variantId) => {
      items.push({
        id: variantId,
        quantity: item.quantity
      });
    });
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }
      
      const data = await response.json();
      
      // Clear selection
      document.querySelectorAll('.variant-checkbox:checked').forEach(checkbox => {
        checkbox.checked = false;
        checkbox.closest('.variant-row').classList.remove('selected');
      });
      
      this.selectedVariants.clear();
      this.updateBulkSelectionUI();
      
      // Show success
      button.textContent = 'Added to cart!';
      button.classList.add('success');
      
      setTimeout(() => {
        button.disabled = false;
        button.textContent = 'Add Selected to Cart';
        button.classList.remove('success');
      }, 2000);
      
      this.showNotification(`Added ${items.length} items to cart!`, 'success');
      this.updateCartBubble();
      this.openCartDrawer();
      
    } catch (error) {
      console.error('Error adding items to cart:', error);
      button.disabled = false;
      button.textContent = 'Add Selected to Cart';
      this.showNotification('Failed to add items to cart. Please try again.', 'error');
    }
  }

  /**
   * Update cart UI elements
   */
  updateCartUI(data) {
    // Trigger cart update event
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
  }
  
  /**
   * Update cart bubble count
   */
  async updateCartBubble() {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      });
      const cart = await response.json();
      
      console.log('Cart contents:', cart);
      
      // Update the cart-count custom element (primary header cart icon)
      const cartCountElement = document.querySelector('cart-count');
      if (cartCountElement) {
        // Update the visible count
        const countSpan = cartCountElement.querySelector('span[aria-hidden="true"]');
        if (countSpan) {
          countSpan.textContent = cart.item_count;
        }
        
        // Update screen reader text
        const srSpan = cartCountElement.querySelector('.sr-only');
        if (srSpan) {
          srSpan.textContent = srSpan.textContent.replace(/\d+/, cart.item_count);
        }
        
        // Show/hide the count bubble based on item count
        if (cart.item_count > 0) {
          cartCountElement.classList.remove('opacity-0');
        } else {
          cartCountElement.classList.add('opacity-0');
        }
      }
      
      // Update all other cart count elements (for backward compatibility)
      document.querySelectorAll('.cart-count-bubble, .cart-count, .cart-count__bubble').forEach(element => {
        if (element && element.tagName !== 'CART-COUNT') {
          const countSpan = element.querySelector('span') || element;
          countSpan.textContent = cart.item_count;
          
          // Show/hide based on count
          if (cart.item_count > 0) {
            element.classList.remove('hide');
            element.style.display = '';
          } else {
            element.classList.add('hide');
          }
        }
      });
      
      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: cart }
      }));
    } catch (error) {
      console.error('Error updating cart bubble:', error);
    }
  }

  /**
   * Refresh cart drawer content
   */
  async refreshCartDrawer() {
    try {
      // Fetch the cart sections
      const response = await fetch('/cart?sections=cart-drawer,cart-icon-bubble');
      const sections = await response.json();
      
      // Update cart drawer HTML if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && sections['cart-drawer']) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sections['cart-drawer'];
        const newCartContent = tempDiv.querySelector('cart-drawer');
        if (newCartContent) {
          cartDrawer.innerHTML = newCartContent.innerHTML;
        }
      }
      
      // Update cart icon bubble
      if (sections['cart-icon-bubble']) {
        const cartIconBubble = document.querySelector('.header__icon--cart');
        if (cartIconBubble) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sections['cart-icon-bubble'];
          const newBubble = tempDiv.querySelector('.cart-count-bubble');
          const currentBubble = cartIconBubble.querySelector('.cart-count-bubble');
          if (newBubble && currentBubble) {
            currentBubble.replaceWith(newBubble);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }
  
  /**
   * Open cart drawer
   */
  openCartDrawer() {
    // Dispatch cart drawer open event
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    // Try to open cart drawer component
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Try multiple methods to open the drawer (same as cart-improvements.js)
      if (typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      } else if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer.setAttribute('open', '');
      }
    }

    // Or trigger click on cart icon
    const cartIcon = document.querySelector('[aria-controls="cart-drawer"], .header__icon--cart, .cart-link');
    if (cartIcon) {
      cartIcon.click();
    }
  }

  /**
   * Show notification
   */
  showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `ticket-notification ${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        ${type === 'success' ? 
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>' :
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
        }
        <span>${message}</span>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Trigger animation
    setTimeout(() => notification.classList.add('show'), 10);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Stamp cards with data-sort-order for default order restoration
   */
  stampDefaultOrder() {
    const grid = document.querySelector('.ticket-cards-grid');
    if (!grid) return;
    const cards = grid.querySelectorAll('.ticket-variant-card');
    cards.forEach((card, i) => {
      card.setAttribute('data-sort-order', i);
    });
  }

  /**
   * Setup sort controls for main ticket grid
   */
  setupSortControls() {
    const select = document.getElementById('ticket-sort-select');
    const grid = document.querySelector('.ticket-cards-grid');
    if (!select || !grid) return;

    select.addEventListener('change', () => {
      const cards = Array.from(grid.querySelectorAll('.ticket-variant-card'));
      const value = select.value;

      if (value === 'price-asc') {
        cards.sort((a, b) => (parseFloat(a.dataset.price) || 0) - (parseFloat(b.dataset.price) || 0));
      } else if (value === 'price-desc') {
        cards.sort((a, b) => (parseFloat(b.dataset.price) || 0) - (parseFloat(a.dataset.price) || 0));
      } else {
        cards.sort((a, b) => (parseInt(a.dataset.sortOrder) || 0) - (parseInt(b.dataset.sortOrder) || 0));
      }

      cards.forEach(card => grid.appendChild(card));
    });
  }

  /**
   * Initialize countdown timer on sticky bar
   */
  initCountdown() {
    const el = document.getElementById('sticky-countdown');
    if (!el) return;

    const eventDateMs = parseInt(el.dataset.eventDate);
    if (!eventDateMs || isNaN(eventDateMs)) return;

    const update = () => {
      const now = Date.now();
      const diff = eventDateMs - now;
      if (diff <= 0) {
        el.textContent = 'Event day!';
        return;
      }
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      el.textContent = `${days} day${days !== 1 ? 's' : ''} to go`;
    };

    update();
    // Refresh hourly
    setInterval(update, 3600000);
  }

  /**
   * Compute deal scores based on category tier / normalized price
   */
  computeDealScores() {
    const TIER_MAP = {
      'box-loge': 6, 'category-gold': 5, 'category-1': 4,
      'category-2': 3, 'category-3': 2
    };

    // Get original cards only (not sidebar clones)
    const allCards = document.querySelectorAll('.ticket-variant-card');
    const cards = Array.from(allCards).filter(c => !c.closest('#seating-map-tickets'));

    if (cards.length === 0) return;

    // Group prices by category
    const categoryPrices = {};
    cards.forEach(card => {
      const cat = card.dataset.category;
      const price = parseFloat(card.dataset.price);
      if (!cat || isNaN(price) || price <= 0) return;
      if (!categoryPrices[cat]) categoryPrices[cat] = [];
      categoryPrices[cat].push({ card, price });
    });

    const catKeys = Object.keys(categoryPrices);
    // Guard: skip if fewer than 3 categories
    if (catKeys.length < 3) return;

    // Compute median per category
    const medians = {};
    let allPrices = [];
    catKeys.forEach(cat => {
      const prices = categoryPrices[cat].map(c => c.price).sort((a, b) => a - b);
      const mid = Math.floor(prices.length / 2);
      medians[cat] = prices.length % 2 !== 0 ? prices[mid] : (prices[mid - 1] + prices[mid]) / 2;
      allPrices = allPrices.concat(prices);
    });

    // Overall median
    allPrices.sort((a, b) => a - b);
    const overallMid = Math.floor(allPrices.length / 2);
    const overallMedian = allPrices.length % 2 !== 0
      ? allPrices[overallMid]
      : (allPrices[overallMid - 1] + allPrices[overallMid]) / 2;

    if (overallMedian <= 0) return;

    // Score each card
    const scores = [];
    cards.forEach(card => {
      const cat = card.dataset.category;
      const price = parseFloat(card.dataset.price);
      const tier = TIER_MAP[cat];
      if (!tier || isNaN(price) || price <= 0 || !medians[cat]) return;

      const score = tier / (medians[cat] / overallMedian);
      scores.push({ card, score });
    });

    if (scores.length === 0) return;

    // Sort by score descending to find thresholds
    const sorted = scores.map(s => s.score).sort((a, b) => b - a);
    const greatThreshold = sorted[Math.max(0, Math.floor(sorted.length * 0.15) - 1)];
    const goodThreshold = sorted[Math.max(0, Math.floor(sorted.length * 0.30) - 1)];

    scores.forEach(({ card, score }) => {
      // Skip if badge already exists
      if (card.querySelector('.deal-score-badge')) return;

      let badge = null;
      if (score >= greatThreshold) {
        badge = document.createElement('span');
        badge.className = 'deal-score-badge deal-score-badge--great';
        badge.textContent = 'Great Deal';
      } else if (score >= goodThreshold) {
        badge = document.createElement('span');
        badge.className = 'deal-score-badge deal-score-badge--good';
        badge.textContent = 'Good Value';
      }

      if (badge) {
        const indicators = card.querySelector('.value-indicators');
        if (indicators) {
          indicators.appendChild(badge);
        }
      }
    });
  }

  /**
   * Format money
   */
  formatMoney(cents) {
    if (window.CurrencyConverter && window.CurrencyConverter.isEurMode()) {
      return window.CurrencyConverter.convert(cents);
    }
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

  updatePricesForCurrency() {
    document.querySelectorAll('[data-usd-cents]').forEach(el => {
      const cents = parseInt(el.dataset.usdCents, 10);
      if (!isNaN(cents)) {
        el.textContent = this.formatMoney(cents);
      }
    });
  }

  /**
   * Observe cart updates from other sources
   */
  observeCartUpdates() {
    document.addEventListener('cart:updated', (e) => {
      // Update any UI elements if needed
      console.log('Cart updated:', e.detail);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new TicketVariantsTable();
  });
} else {
  new TicketVariantsTable();
}

// Add notification styles
const notificationStyles = document.createElement('style');
notificationStyles.textContent = `
  .ticket-notification {
    position: fixed;
    top: 20px;
    right: -400px;
    background: white;
    border-radius: 12px;
    padding: 1rem 1.5rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: right 0.3s ease;
    z-index: 99999;
    max-width: 90%;
  }
  
  .ticket-notification.show {
    right: 20px;
  }
  
  .ticket-notification.success {
    border-left: 4px solid #10b981;
  }
  
  .ticket-notification.error {
    border-left: 4px solid #ef4444;
  }
  
  .ticket-notification .notification-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .ticket-notification.success svg {
    color: #10b981;
  }
  
  .ticket-notification.error svg {
    color: #ef4444;
  }
  
  .variant-row.selected {
    background: linear-gradient(135deg, #f0f4ff 0%, #e8edff 100%) !important;
  }
  
  .variant-checkbox {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  
  @media (max-width: 768px) {
    .ticket-notification {
      top: auto;
      bottom: 20px;
      right: 10px;
      left: 10px;
      max-width: calc(100% - 20px);
    }
    
    .ticket-notification.show {
      right: 10px;
    }
  }
`;
document.head.appendChild(notificationStyles);
}
